// @ts-strict-ignore
import type React from 'react';
import type { Option, Drawer } from '@usealloy/component-library';
import type { Attribute, NodeEditorTab } from 'flow/store/engine/types';
import type { CaretIndexType } from '../useCaretControl/constants';

// Drawer tab with only specific values allowed for `value`
export type NodeEditorDrawerTabConfig = React.ComponentProps<typeof Drawer>['tabs'][number] & {
  value: NodeEditorTab;
};

export enum RuleEditorDropdownMode {
  EXPRESSION = 'expression',
  DATAROUTE_SOURCE = 'dataRouteSource',
  DATAROUTE_ATTRIBUTE = 'dataRouteAttribute',
  CARET_EXPRESSION = 'caretExpression',
  GROUP_START = 'groupStart',
  GROUP_START_PENDING = 'groupStartPending',
  GROUP_END = 'groupEnd',
  MLA_COMPONENT = 'mlaComponent',
  CARET_MLA_COMPONENT = 'caretMlaComponent',
}

export enum RuleEditorDropdownModeCategory {
  DATAROUTE = 'dataRoute',
  GROUP = 'group',
}

export type UserActionObject = {
  meta?: string;
  value?: string;
  mode?: string;
};

export type CaretPositionGetterReturnValues = {
  newPositionString: string;
  newPositionIndexForDelayedUpdate: string;
};

export const CaretPositionSuffixTypes = {
  CARET_INSERTION: '!caret',
  DATAROUTE: '!dataRoute',
  GROUP_START: '!groupStart',
  GROUP_START_PENDING: '!groupStartPending',
  GROUP_END: '!groupEnd',
  MLA_REQUIRED: '!mlaRequired',
  MLA_SELECT_ONE: '!mlaSelectOne',
  MLA_MULTI_SELECT: '!mlaMultiSelect',
  MLA_CSV: '!mlaCsv',
  MLA_INTEGER: '!mlaInteger',
  MLA_BOOLEAN: '!mlaBoolean',
  MLA_VALUE_SET: '!mlaValueSet',
};

export type RuleItemRef = { path: string; width: number };
export type RuleItemRefs = Record<string, { width: number }>;

export type DropdownOption = Option<{
  shortenedLabel?: string;
  category?: 'string' | 'decimal' | 'attribute' | 'action' | 'operator' | 'grouping' | 'function'
  | 'functionSeparator';
  hideIcon?: boolean;
  RHS?: string | number | null;
  isMLA?: boolean;
  showAttributeWidget?: boolean;
  // used to resolve aggregation object type + populate attribute widget
  attribute?: Attribute;
  // the action's type, so an option can be coloured by what it points at
  actionType?: string;
}>;

export type SemanticLine = {
  id: string | number;
  items: string[];
  width: number;
  mlaEditorInThisLine?: boolean;
};

export enum ThresholdRightAttributeOrigin {
  THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REGEX = 'thresholdRightAttributeValueRegex',
  THRESHOLD_RIGHT_ATTRIBUTE_VALUE_LIST_INCLUSION = 'thresholdRightAttributeValueListInclusion',
}

// TODO(OAK-1232): Use this enum once Aggregations support dataroutes
export enum MLAAttributeItemOrigin {
  THRESHOLD_LEFT = 'thresholdLeft',
  THRESHOLD_RIGHT = 'thresholdRight',
  DATAROUTE = 'dataRoute',
}

export enum AggregationEditorOrigin {
  THRESHOLD_LEFT = 'thresholdLeft',
  THRESHOLD_RIGHT = 'thresholdRight',
}

export type IsolatedEditingBlockOrigin = ThresholdRightAttributeOrigin | MLAAttributeItemOrigin;

export type IsolatedEditingBlockData = {
  originPublicId: string;
  originCaretIndex?: string;
  originType?: IsolatedEditingBlockOrigin;
};

export type CaretControlContext = {
  morphingCaretPosition: string;
  setMorphingCaretPosition: (_position: string) => void;
  getFlattenedCaretPositionIndex: ({
    itemPath,
    mode,
    insertion,
    type,
  }: {
    itemPath: string;
    mode?: RuleEditorDropdownMode;
    insertion?: boolean;
    type: CaretIndexType;
  }) => string | undefined;
  pendingMorphingCaretPositionUpdateValue: string | undefined;
  setPendingMorphingCaretPositionUpdateValue: (_pendingPositionIdx: string) => void;
  activeInputValue: {
    value: string;
    path: string;
    initialValue: boolean;
  };
  setActiveInputValue: (_inputObject: {
    value: string;
    path: string;
    initialValue: boolean;
  }) => void;
  getPreviousCaretPosition: ({
    userActionObject,
  }: { userActionObject?: UserActionObject }) => CaretPositionGetterReturnValues | undefined;
  getNextCaretPosition: ({
    userActionObject,
  }: { userActionObject?: UserActionObject }) => CaretPositionGetterReturnValues | undefined;
  getNewPositionToJump: ({
    userActionObject,
  }: { userActionObject?: UserActionObject }) => CaretPositionGetterReturnValues | undefined;
};
